import React, { useMemo } from 'react';
import { Box, Stack, Typography, useTheme, alpha } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';

/**
 * Rolling live-strength sparkline (spec §7 J3, M14). Plots the raw
 * 15-minute strength measurements with the EWMA on top, the validation
 * baseline as a dashed reference, and a marker where CUSUM confirmed a
 * change-point.
 */
const StrengthSparkline = ({ rolling, height = 120 }) => {
  const theme = useTheme();
  const history = useMemo(() => rolling?.history || [], [rolling]);

  const { series, markers } = useMemo(() => {
    const strength = [];
    const ewma = [];
    history.forEach((r, i) => {
      if (r.strength != null) strength.push({ x: i, y: r.strength });
      if (r.ewma != null) ewma.push({ x: i, y: r.ewma });
    });
    const m = [];
    if (rolling?.baseline_corr != null) {
      m.push({
        axis: 'y',
        value: rolling.baseline_corr,
        lineStyle: {
          stroke: alpha(theme.palette.text.primary, 0.35),
          strokeDasharray: '4 4',
          strokeWidth: 1,
        },
        legend: 'baseline',
        legendOrientation: 'horizontal',
        textStyle: { fontSize: 9, fill: theme.palette.text.secondary },
      });
    }
    if (rolling?.changepoint_at != null) {
      const idx = history.findIndex((r) => r.t >= rolling.changepoint_at);
      if (idx >= 0) {
        m.push({
          axis: 'x',
          value: idx,
          lineStyle: { stroke: theme.palette.error.main, strokeWidth: 1.5 },
          legend: 'change-point',
          textStyle: { fontSize: 9, fill: theme.palette.error.main },
        });
      }
    }
    return {
      series: [
        { id: 'strength', data: strength },
        { id: 'ewma', data: ewma },
      ],
      markers: m,
    };
  }, [history, rolling, theme]);

  if (history.length < 2) {
    return (
      <Typography variant="caption" color="text.secondary">
        Not enough live measurements yet — the live loop samples this edge
        every 15 minutes.
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ height }}>
        <ResponsiveLine
          data={series}
          margin={{ top: 10, right: 12, bottom: 8, left: 36 }}
          xScale={{ type: 'linear' }}
          yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
          curve="monotoneX"
          colors={[alpha(theme.palette.primary.main, 0.35), theme.palette.primary.main]}
          lineWidth={1.5}
          enablePoints={false}
          enableGridX={false}
          gridYValues={3}
          axisBottom={null}
          axisLeft={{ tickSize: 0, tickValues: 3 }}
          markers={markers}
          animate={false}
          isInteractive={false}
          theme={{
            text: { fill: theme.palette.text.secondary, fontSize: 10 },
            grid: { line: { stroke: alpha(theme.palette.text.primary, 0.06) } },
          }}
        />
      </Box>
      <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
        <Legend color={alpha(theme.palette.primary.main, 0.35)} label="measured" />
        <Legend color={theme.palette.primary.main} label="EWMA" />
        {rolling?.ewma_corr != null && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto !important' }}>
            now {rolling.ewma_corr} vs baseline {rolling.baseline_corr ?? '—'}
          </Typography>
        )}
      </Stack>
    </>
  );
};

const Legend = ({ color, label }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <Box sx={{ width: 12, height: 2, bgcolor: color, borderRadius: 1 }} />
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Stack>
);

export default StrengthSparkline;
